import React from 'react';
import { Link } from 'react-router-dom';
import { FaBars } from 'react-icons/fa';
import { SparklesIcon, LockClosedIcon } from '@heroicons/react/24/solid';
import { FiLogOut } from 'react-icons/fi';
import { useDispatch, useSelector } from 'react-redux';
import { toggleCashbot } from '../../../../app/store/slices/adminSlice';
import { toggleUserCashbot, selectUserCashbot, selectAdStatus, checkAdStatus } from '../../../../app/store/slices/userDashboardSlice';
import { selectCurrentPlan } from '../../../../app/store/slices/subscriptionSlice';
import logo from '../../../../assets/images/logos/576_Cash Canvas-02.png';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';

const Navbar = ({ onToggleSidebar, isAdmin = false }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const currentPlan = useSelector(selectCurrentPlan);
  const adminCashbot = useSelector((state) => state.admin?.cashbot);
  const userCashbot = useSelector(selectUserCashbot);
  const adStatus = useSelector(selectAdStatus);
  const cashbot = isAdmin ? adminCashbot : userCashbot;
  const selectedCanvas = useSelector((state) => state.canvas.selectedCanvas);    
  // Check if user is on free plan (only matters for regular users, not admins)
  const isFreeUser = !isAdmin && currentPlan?.plan?.planKey === 'free';

  const handleLogout = () => {
    try {
      localStorage.removeItem('authData');
      localStorage.removeItem('canvasData');
      localStorage.removeItem('selectedCanvas');
      localStorage.removeItem('pendingInvitation');

      Object.keys(localStorage).forEach((key) => {
        if (
          key.startsWith('ad_status_') ||
          key.startsWith('ad_views_') ||
          key.startsWith('last_sync_') ||
          key.startsWith('user_cashbot_') ||
          key.startsWith('user_widgets_') ||
          key.startsWith('cashbot_history_')
        ) {
          localStorage.removeItem(key);
        }
      });

      toast.success('Logged out successfully');
      navigate('/');
    } catch (error) {
      console.error('Logout failed:', error);
      toast.error('Failed to logout. Please try again.');
    }
  };

  const handleCashbotToggle = () => {
    if (isAdmin) {
      // Admin always has access to Cashbot
      dispatch(toggleCashbot());
    } else if (isFreeUser) {
      if (adStatus.hasAccess) {
        // Free user with access can use cashbot
        dispatch(toggleUserCashbot());
      } else {
        // Free user without access - check ad status and notify
        dispatch(checkAdStatus(selectedCanvas?.id))
          .then(() => {
            toast.info('Watch ads to unlock CashBot access or upgrade to a premium plan');
            navigate('/dashboard'); // Navigate to dashboard to show the ad widget
          });
      }
    } else {
      // Paid users can access user cashbot
      dispatch(toggleUserCashbot());
    }
  };
  
  const AiIcon = () => (
    <div className="flex items-center justify-center space-x-2">
      {isFreeUser && !adStatus.hasAccess ? (
        <LockClosedIcon className="w-6 h-6 text-white" />
      ) : (
        <SparklesIcon className="w-6 h-6 text-white" />
      )}
      <span className="hidden md:inline text-white">CashBot</span>
    </div>
  );

  return (
    <nav className="bg-btn-primary px-4 md:px-6 py-2 flex justify-between items-center shadow-sm">
      <div className="flex items-center gap-2 md:gap-4">
        {/* Mobile Toggle Button */}
        <button
          onClick={onToggleSidebar}
          className="p-2 rounded-md hover:bg-btn-secondary-hover"
        >
          <FaBars className="text-white text-xl" />
        </button>
        <img 
        src={logo} alt="Cash Canvas" 
        className="h-16 md:h-20 cursor-pointer" 
        onClick={() => navigate(isAdmin ? '/manage-portal' : '/user-dashboard')}
        />
      </div>
      <div className="flex items-center gap-2 md:gap-4">
        <button 
          onClick={handleCashbotToggle}
          className={`px-3 md:px-4 py-1.5 text-white rounded-lg text-sm md:text-base 
            font-sans font-bold transition-colors ${
              isFreeUser && !adStatus.hasAccess
                ? 'bg-gray-500 hover:bg-gray-600'
                : cashbot?.isOpen 
                  ? 'bg-btn-primary hover:bg-btn-primary-hover' 
                  : 'bg-btn-secondary hover:bg-btn-secondary-hover'
            }`}
          title={isFreeUser && !adStatus.hasAccess ? "Watch Ads to Access CashBot" : "Open CashBot"}
        >
           <AiIcon />
        </button>
        <Link 
          to="/settings"
          className="px-3 md:px-4 py-1.5 bg-btn-primary text-white rounded-lg text-sm md:text-base
            font-sans font-bold hover:bg-btn-primary-hover transition-colors"
        >
          Settings
        </Link>
        <Link to="/" onClick={handleLogout}>
          <FiLogOut className="text-white text-2xl rounded-lg" />
        </Link>
      </div>
    </nav>
  );
};

export default Navbar;
